<template>
  <LayoutBaseContent class="pb-64 max-w-8xl sm:pb-12">
    <WidgetBreadcrumbs :isLoading="listingApi.loading" :crumbs="compBreadcrumbs" class="mt-4 mb-4" />

    <div v-if="!this.listingApi.loading && !listing.title">
      <UtilEmpty
        :icon="faUfoBeam"
        title="That listing can't be found"
        message="This listing is not currently available. If you own this listing please login"
        class="self-start max-w-xl px-4 py-6 mx-4 mt-4 bg-white rounded sm:px-8 sm:mx-auto sm:mt-8"
      />
    </div>

    <template v-else>
      <div class="flex justify-center w-full">
        <WidgetAdvert
          v-if="!compIsLoading"
          shape="banner"
          :advertIndex="0"
          :listingRef="listing.listingRef"
          isExpandable
          class="mb-8 sm:absolute"
          :class="{ 'mx-2': $breakpoint.mdAndDown }"
        />
      </div>

      <template v-if="compIsLoading">
        <UtilPlaceholder class="w-8/12 h-6 mb-2" />
        <UtilPlaceholder class="w-6/12 h-6 mb-2 md:hidden" />
        <UtilPlaceholder class="w-3/12 h-4 mb-4" />
      </template>

      <div v-else class="flex-wrap p-2 sm:p-0 xs:flex xs:justify-between xs:items-center sm:mt-64">
        <template v-if="listingIs.matchmaking && listingIs.owner">
          <h1 class="mb-2 text-2xl font-medium">Thank you for requesting our Matchmaker service</h1>
        </template>

        <UtilMessage v-else-if="$route.query.premiumActivate" type="success" class="w-full mb-4">
          Premium Listing Activated! Thanks for upgrading - one of our Sales Expert will contact you shortly to
          support your sale.
        </UtilMessage>

        <template v-else>
          <h1 class="mb-2 text-2xl font-medium">
            <span class="flex flex-wrap items-center gap-x-2">
              <span v-if="listingIs.sold" class="text-primary-button-darker">Sold</span>

              <span v-else-if="listing.type === listingType.WANTED" class="text-danger">Wanted</span>

              <UtilTagAuction v-else-if="listing.type === listingType.AUCTION" />

              <span v-else class="text-brand">For Sale</span>

              <span>{{ compTitle }}</span>
            </span>

            <div class="flex items-center mb-1 text-base font-normal">
              <UtilLocationSummary
                :location="listing.location[0]"
                :showLocality="listingIs.detailsVisible"
                flagSize="sm"
                targetClass="text-base font-normal"
              />
            </div>
          </h1>

          <div>
            <WidgetListingShare :listing="listing" class="mr-4 text-base" />

            <WidgetListingWatch
              v-if="!listingIs.owner && listingIs.live"
              :listingRef="listing.listingRef"
              class="mr-4 text-base text-primary"
            />

            <UtilButton
              v-if="!compIsLoading && !listingIs.owner"
              @click="reportListing()"
              color="secondary"
              textLink
              class="text-base"
            >
              <FontAwesomeIcon :icon="faExclamationTriangle" fixed-width />
              <span>{{ $t('buttons.report') }}</span>
            </UtilButton>
          </div>
        </template>
      </div>

      <template v-if="listingIs.dead">
        <UtilMessage type="information" class="mx-2 mb-6 sm:mx-0">
          <template v-if="listingIs.owner">
            This listing has finished and cannot be edited.
            <UtilTextLink to="/account/dashboard">Return to your dashboard</UtilTextLink>

            <div class="mt-4">
              <UtilButton :to="`/post/listing/similar/${listing.listingRef}`" color="primary">
                <FontAwesomeIcon :icon="faPlusCircle" />
                <span class="ml-1">Post similar listing</span>
              </UtilButton>
            </div>
          </template>

          <template v-else-if="listingIs.sold">
            This listing has sold.
            <UtilTextLink :to="compFindSimilarSearchLink">Find similar listings</UtilTextLink>
          </template>

          <template v-else>
            This listing has now ended.
            <UtilTextLink :to="compFindSimilarSearchLink">Find similar listings</UtilTextLink>
          </template>
        </UtilMessage>
      </template>

      <div v-if="!compIsLoading && listingIs.owner && !listingIs.dead" class="mb-4">
        <ListingManage
          :isLoading="compIsLoading"
          @refresh="loadListing(false)"
          :listing="listing"
          :listingIs="listingIs"
          :title="compTitle"
        />
      </div>

      <ListingAdminToolBox
        v-if="appIs.god && !listingIs.owner && $breakpoint.mdAndUp"
        :isLoading="compIsLoading"
        :listing="listing"
        :listingIs="listingIs"
        :title="compTitle"
        class="mb-4"
      />

      <div class="flex flex-wrap lg:flex-no-wrap">
        <!-- <UtilGallery :media="compMedia" :isLoading="compIsLoading" /> -->
        <div class="flex-auto w-full lg:w-auto">
          <UtilListingGallery
            :isLoading="compIsLoading"
            :listing="listing"
            :images="compMedia.images"
            :videos="compMedia.videos"
          />

          <LayoutPanel
            v-if="$breakpoint.lgAndUp"
            id="listing-left-column"
            class="self-start flex-grow w-full mt-8"
          >
            <ListingDetails
              v-if="!compIsLoading"
              :isLoading="compIsLoading"
              :listing="listing"
              :listingIs="listingIs"
              class="container-padding"
            />
          </LayoutPanel>

          <!-- <UtilListingGallery
            v-if="$breakpoint.mdAndDown"
            :listing="listing"
            :images="compMedia.images"
            :videos="compMedia.videos"
            :isLoading="compIsLoading"
          /> -->
        </div>

        <!-- SIDEBAR -->
        <div class="flex-auto flex-grow mt-8 lg:mt-0 listing-right-column-container lg:ml-8">
          <div id="listing-right-column">
            <UtilTextLink
              v-if="!compIsLoading && listing.portals && listing.portals.length && listing.portals[0].approved"
              :to="`/portal/${listing.portals[0].link}`"
              class="relative block h-48 max-w-full mb-4 overflow-hidden shadow sm:rounded"
            >
              <UtilGradientOverlay :opacityWhite="0.7" class="sm:rounded z-2" />

              <UtilImage
                :fileRef="listing.portals[0].bannerFileRef"
                :width="490"
                :height="200"
                crop
                cover
                :rounded="$breakpoint.smAndUp"
                class="h-full z-1"
              />

              <div class="absolute top-0 bottom-0 left-0 right-0 px-4 py-4 text-center z-3">
                <h4 class="text-2xl font-medium">{{ listing.portals[0].name }}</h4>

                <UtilButton color="secondary" small class="mt-2">
                  View {{ listing.portals[0].portalType === portalType.BREED ? 'Society' : 'More' }}
                </UtilButton>
              </div>
            </UtilTextLink>

            <LayoutPanel v-if="compIsLoading || (!listingIs.timeRestricted && appIs.auth)" class="mb-6">
              <div class="container-padding">
                <WidgetUac
                  v-if="!compIsLoading"
                  :user="compUser"
                  :company="compCompany"
                  :linkToProfile="
                    (!listingIs.timeRestricted && appIs.auth && listingIs.detailsVisible) || listingIs.owner
                  "
                  :showFlag="false"
                  :bordered="false"
                  :showStats="
                    (!listingIs.timeRestricted && appIs.auth && listingIs.detailsVisible) || listingIs.owner
                  "
                  :locked="
                    !listingIs.owner &&
                    !listingIs.auction &&
                    (listingIs.timeRestricted || !listingIs.detailsVisible)
                  "
                  showLockedButton
                  showMembershipStatus
                  :class="{ 'disabled cursor-pointer': listingIs.timeRestricted || !appIs.auth }"
                  :data-disabled-label="
                    listingIs.timeRestricted
                      ? $t('listing.sellerUnlocks', { hoursLeft: listingIs.timeRestrictedHoursLeft })
                      : $t('listing.sellerLogin')
                  "
                  locationOrigin="listing"
                />

                <UtilPlaceholderUac v-else />
              </div>
            </LayoutPanel>

            <LayoutPanel>
              <div class="container-padding">
                <!-- Overview -->
                <ListingOverview
                  ref="listingOverview"
                  :isLoading="compIsLoading"
                  :listing="listing"
                  :listingIs="listingIs"
                  :title="compTitle"
                  :event="event"
                />
              </div>
            </LayoutPanel>

            <LayoutPanel class="mt-6" v-if="listingIs.auction">
              <div class="container-padding">
                <h3 class="mb-4 text-2xl font-medium">Auction help</h3>

                <div class="space-y-4">
                  <UtilButton color="outline" @click="onAuctionAskQuestionButtonClick()">
                    <FontAwesomeIcon :icon="faComments" />
                    Ask us a question
                  </UtilButton>

                  <div class="font-medium">
                    <FontAwesomeIcon :icon="faQuestionCircle" class="mr-1" />
                    Need help on how auctions work?
                    <UtilTextLink to="/help/how-it-works-auctions" class="block sm:inline-block text-brand">
                      Visit our help centre
                    </UtilTextLink>
                  </div>
                </div>
              </div>
            </LayoutPanel>

            <template v-if="$breakpoint.mdAndDown">
              <WidgetAdvert
                v-if="!compIsLoading"
                :listingRef="listing.listingRef"
                key="advert-small-0"
                class="px-2 my-4"
              />

              <LayoutPanel class="pb-8 mt-4">
                <ListingDetails
                  v-if="!compIsLoading"
                  :isLoading="compIsLoading"
                  :listing="listing"
                  :listingIs="listingIs"
                  class="container-padding"
                />
              </LayoutPanel>
            </template>

            <WidgetAdvert
              v-if="$breakpoint.mdAndUp && !compIsLoading"
              shape="skyscraper"
              :advertIndex="0"
              :listingRef="listing.listingRef"
              class="mx-auto my-4 fit-content"
            />

            <LayoutPanel
              v-if="!listingIs.auction && !listingIs.owner && appIs.auth && listingIs.forSale"
              transparent
              class="mt-2 -mb-2 sm:-mb-4"
            >
              <WidgetListingFeedback :listingRef="listing.listingRef" />
            </LayoutPanel>

            <ListingRelated
              ref="relatedListings"
              @loaded="triggerColumnBalancing()"
              :isLoading="compIsLoading"
              :listingIs="listingIs"
              :listing="listing"
              class="mt-4 sm:mt-8"
            />

            <transition v-if="!isSidebarTooLong" name="fade" mode="out-in">
              <LayoutPanel v-show="showSidebarStickyOverview" class="sticky mt-8" style="top: 20px">
                <div class="container-padding">
                  <ListingOverview
                    :isLoading="compIsLoading"
                    :listing="listing"
                    :listingIs="listingIs"
                    :title="compTitle"
                    :event="event"
                  />
                </div>
              </LayoutPanel>
            </transition>
          </div>
        </div>

        <!-- <div v-if="$breakpoint.mdAndUp">
          <WidgetAdvert
            v-if="$breakpoint.mdAndUp && !compIsLoading"
            shape="skyscraper"
            :advertIndex="0"
            :listingRef="listing.listingRef"
            class="sticky inline-block mb-4 top-10 md:ml-4 lg:ml-8"
          />
        </div> -->
      </div>
    </template>
  </LayoutBaseContent>
</template>

<script>
import {
  faBullseyePointer,
  faCog,
  faExclamationTriangle,
  faHourglassHalf,
  faPencil,
  faPlusCircle,
  faTrashAlt,
  faUfoBeam,
  faComments,
  faQuestionCircle
} from '@fortawesome/pro-regular-svg-icons'
import { faGavel } from '@fortawesome/pro-solid-svg-icons'
import { debounce, get } from 'lodash'
import { mapState } from 'vuex'
import qs from 'qs'
import { generateMetaTags } from '@/lib/generate-meta'
import LegacyApi from '@/lib/api/legacy'
import { displayLocation, listingType, portalType } from '@/lib/mapping'
import generateFileRefOgImage from '@/lib/og-image-fileref'
import generateMapImageUrl from '@/lib/map-image'
import { fileExtensions } from '@/lib/file-types'
import LayoutBaseContent from '@/components/layout/LayoutBaseContent'
import LayoutPanel from '@/components/layout/LayoutPanel'
import UtilPlaceholder from '@/components/utils/placeholders/UtilPlaceholder'
import WidgetBreadcrumbs from '@/components/widgets/WidgetBreadcrumbs'
import UtilButton from '@/components/utils/UtilButton'
import UtilEmpty from '@/components/utils/UtilEmpty'
import UtilMessage from '@/components/utils/UtilMessage'
import UtilImage from '@/components/utils/UtilImage'
import UtilTextLink from '@/components/utils/UtilTextLink'
import UtilGradientOverlay from '@/components/utils/UtilGradientOverlay'
import UtilLocationSummary from '@/components/utils/UtilLocationSummary'
import ListingDetails from '@/views/ListingDetails'
import ListingRelated from '@/views/ListingRelated'
import WidgetListingShare from '@/components/widgets/WidgetListingShare'
import WidgetListingWatch from '@/components/widgets/WidgetListingWatch'
import ListingOverview from '@/views/ListingOverview'
import WidgetAdvert from '@/components/widgets/WidgetAdvert'
import WidgetListingFeedback from '@/components/widgets/WidgetListingFeedback'
import WidgetUac from '@/components/widgets/WidgetUac'
import UtilPlaceholderUac from '@/components/utils/placeholders/UtilPlaceholderUac'
import UtilTagAuction from '@/components/utils/UtilTagAuction'
import UtilListingGallery from '@/components/utils/listing/gallery/UtilListingGallery'

export default {
  name: 'Listing',
  components: {
    LayoutBaseContent,
    LayoutPanel,
    WidgetBreadcrumbs,
    UtilButton,
    UtilEmpty,
    UtilMessage,
    UtilImage,
    UtilTextLink,
    UtilGradientOverlay,
    UtilLocationSummary,
    ListingDetails,
    ListingRelated,
    ListingOverview,
    WidgetListingShare,
    WidgetListingWatch,
    UtilPlaceholder,
    WidgetAdvert,
    WidgetListingFeedback,
    WidgetUac,
    UtilPlaceholderUac,
    UtilTagAuction,
    UtilListingGallery,
    ListingManage: () => import('@/views/ListingManage'),
    ListingAdminToolBox: () => import('@/views/ListingAdminToolBox')
  },
  data() {
    return {
      eventApi: new LegacyApi(),
      bdmApi: new LegacyApi(),
      event: null,
      sidebarObserver: null,
      showSidebarStickyOverview: false,
      isSidebarTooLong: false,
      listingType,
      portalType,
      faExclamationTriangle,
      faHourglassHalf,
      faCog,
      faTrashAlt,
      faGavel,
      faBullseyePointer,
      faUfoBeam,
      faPencil,
      faPlusCircle,
      faComments,
      faQuestionCircle
    }
  },
  beforeRouteUpdate(to, from, next) {
    if (to.params.listingCode !== from.params.listingCode) {
      this.loadListing()
    }

    next()
  },
  watch: {
    'appIs.auth'(isAuth) {
      if (isAuth) {
        this.loadListing()
      }
    },

    '$store.state.modal.lastClosed'(newValue) {
      if (newValue && (newValue.includes('ModalListing') || newValue.includes('ModalPremiumListing'))) {
        this.loadListing(false)
      }
    },

    '$route.path'(newPath, oldPath) {
      if (newPath !== oldPath) {
        this.loadListing()
      }
    }
  },
  created() {
    this.loadListing()

    this.$store.dispatch('payments/fetchRestrictedFeaturesUsage')
  },
  destroyed() {
    if (!this.appIs.mobileOrNative) {
      this.destroySidebarScrollObserver()
    }
  },
  computed: {
    ...mapState({
      listingApi: state => state.listing.get,
      listing: state => state.listing.get.data,
      search: state => state.search
    }),

    listingIs() {
      return this.$store.getters['listing/listingIs'](this.listing)
    },

    compIsLoading() {
      return this.listingApi.loading && !this.listing.title
    },

    compTitle() {
      return this.listing.title ? this.$listingTitle(this.listing) : ''
    },

    compUser() {
      return !this.listing || !this.listing.user || this.listingIs.timeRestricted || !this.appIs.auth
        ? {}
        : this.listing.user[0]
    },

    compCompany() {
      return !this.listing || !this.listing.company || this.listingIs.timeRestricted || !this.appIs.auth
        ? {}
        : this.listing.company[0]
    },

    compMainImage() {
      return get(this.listing, 'images[0].fileRef')
        ? `${this.assetBaseUrl.imageThumbnail}${this.listing.images[0].fileRef}`
        : ''
    },

    compMedia() {
      const media = {
        images: [],
        videos: []
      }

      if (!this.listing || (!this.listing.images && !this.listing.videos)) {
        return media
      } else {
        // Normal Images
        media.images = [...this.listing.images]

        // Videos
        if (this.listing.videos && this.listing.videos.length) {
          this.listing.videos.forEach(video => {
            if (video.fileRef) {
              media.videos.push({
                type: 'video',
                fileRef: video.fileRef,
                ...video
              })
            }
          })
        }

        // Data file images
        if (this.listing.files && this.listing.files.length) {
          this.listing.files.forEach(file => {
            if (fileExtensions.image.includes(file.extension)) {
              media.images.push({
                fileRef: file.fileRef,
                ...file
              })
            }
          })
        }

        // Map Images
        if (this.listing.location && this.listing.listingRef) {
          let generateMapConfig = {}

          if (this.listingIs.detailsVisible) {
            generateMapConfig = {
              width: 1200,
              height: 800,
              zoom: 9,
              listingRef: this.listing.listingRef
            }
          } else {
            generateMapConfig = {
              width: 1200,
              height: 800,
              zoom: 9,
              locality: this.listing.location[0].locality,
              region: this.listing.location[0].region
            }
          }

          media.images.push({
            type: 'image',
            src: generateMapImageUrl({
              ...generateMapConfig
            }),
            key: 'map-image'
          })
        }

        // Portal Image
        if (this.listingIs.inPortal && get(this, 'listing.portals[0].bannerFileRef')) {
          media.images.push({
            fileRef: this.listing.portals[0].bannerFileRef
          })
        }

        return media
      }
    },

    compFindSimilarSearchLink() {
      let params = {}
      let searchUrl = `/${this.listing.commoditySlug}`

      if (this.listing.meta.sexes && this.listing.meta.sexes.length) {
        params.sexes = this.listing.meta.sexes[0]
      }

      if (this.listing.meta.types && this.listing.meta.types.length) {
        params.types = this.listing.meta.types[0]
      }

      if (Object.keys(params).length) {
        searchUrl += `?${qs.stringify(params)}`
      }

      return searchUrl
    },

    compOgImage() {
      if (!this.compMainImage) {
        return false
      }

      return generateFileRefOgImage(this.listing.images[0].fileRef)
    },

    compDescription() {
      if (!this.listing) {
        return undefined
      }

      return this.listing.description || `For Sale on SellMyLivestock: ${this.$listingTitle(this.listing)}`
    },

    compBreadcrumbs() {
      // Did the user just come from a previous search, or are they being linked directly?
      // If they come from search let's not show a tree of breacrumbs, but allow for them to easily get back to those results
      let crumbs = []

      if (this.search.url) {
        crumbs.push({
          label: 'Search',
          to: this.search.url
        })
      } else {
        crumbs.push({
          label: 'Search',
          to: { ...this.$router, query: { ...this.$router.query, menu: true } }
        })
      }

      if (this.listing.title) {
        crumbs.push({
          label: this.$listingTitle(this.listing),
          current: true
        })
      } else {
        crumbs.push({
          label: 'Listing not found',
          current: true
        })
      }

      return crumbs
    },

    compToPostcode() {
      return this.app.locations.length ? this.app.locations[0].code : null
    },

    compIsGeneticsRelatedListing() {
      return this.listingIs.breeding && this.listingIs.bulls
    }
  },
  methods: {
    loadListing(scrollToTop = true) {
      // Given the URL "aberdeen-angus-store-bull-84021707" split by dashes and get the last item "84021707"
      let listingCode = this.$route.params.listingCode.split('-').pop()

      // Don't show the old listing while we are loading the next one
      if (listingCode !== this.listing.listingCode) {
        this.$store.commit('listing/resetGet')
      }

      this.getListing(this.$route.params.listingCode, scrollToTop)
    },

    async getListing(listingCodeUrl, scrollToTop = true) {
      let listingRef = null
      let listingCode = null
      this.event = null

      // Listing code or listing ref in URL
      // v4 UUID test
      if (/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/.test(listingCodeUrl)) {
        listingRef = listingCodeUrl
        listingCode = undefined
      } else {
        listingRef = undefined
        listingCode = listingCodeUrl.split('-').pop()
      }

      try {
        const bustCache = this.listingIs.owner
        await this.$store.dispatch('listing/view', { listingRef, listingCode, bustCache })

        if (this.listingIs.event) {
          this.getEvent()
        }

        this.$emit('updateHead')

        // Save a listing view stat if user is not the owner
        if (!this.listingIs.owner) {
          let view = new LegacyApi()

          view.post('listings.view', {
            listingRef,
            listingCode
          })

          this.$analytics.addEvent({
            category: 'listing',
            action: 'View',
            label: this.listing.listingRef
          })
        }

        this.$store.dispatch('adverts/get', {
          commoditySlug: this.listing.commoditySlug,
          listingRef: this.listing.listingRef,
          displayLocation: displayLocation.LISTING_PAGE
        })

        if (this.appIs.auth) {
          this.$store.dispatch('messages/getMessagesForListing', this.listing.listingRef)
        }

        await this.$nextTick()

        if (!this.appIs.mobileOrNative) {
          this.createSidebarScrollObserver()
        }

        if (scrollToTop) {
          this.$scroll.toTop()
        }

        // If premiumActivate is in the URL, send activation slack message
        if (this.$route.query.premiumActivate) {
          const isBotVisit = document.cookie.indexOf('sml-bot=true') !== -1

          if (isBotVisit || this.appIs.god || this.appIs.loggedInAsHelpdesk || this.appIs.maybeStaff) {
            console.log('Skipping premium listing activation')
            return
          }

          let listingSlackLink = `<${process.env.VUE_APP_SITE_URL}/view/listing/${this.listing.listingRef}|${this.listing.title}>`

          this.$slack.sendMessage(`:rocket: Premium Listing ACTIVATED ${listingSlackLink}`, ['sml-premium'])
        }
      } catch (error) {
        this.$log.debug('Error finding listing', error)
      }
    },

    async getEvent() {
      await this.eventApi
        .get('events.get', {
          includeListings: 0,
          eventRef: this.listing.eventRef
        })
        .catch(() => {})

      let event = get(this.eventApi, 'data[0]')

      if (event) {
        this.event = event
      }
    },

    triggerColumnBalancing: debounce(function () {
      setTimeout(() => this.balanceColumns(), 500)
    }, 1000),

    // Attempt to reduce the number of related listings if the listing lacks detail in the left column
    balanceColumns() {
      // IE 11 can't easily remove element so let's skip
      if (this.appIs.legacyBrowser) {
        return
      }

      // Only balance when there are 2 columns!
      if (this.$breakpoint.mdAndDown) {
        this.isSidebarTooLong = false
        return false
      }

      // We can't call slice() on a NodeList, convert it to a normal array
      let relatedListingElements = Array.from(document.querySelectorAll('.related-listing'))

      if (relatedListingElements.length === 0) {
        return false
      }

      const relatedListingHeight = 104
      const leftHeight = document.getElementById('listing-left-column').offsetHeight
      const rightHeight = document.getElementById('listing-right-column').offsetHeight
      const difference = rightHeight - leftHeight

      // Is the right longer than the left?
      if (difference > 0) {
        this.isSidebarTooLong = true

        let totalRelatedListingsToHide = Math.floor(difference / relatedListingHeight)
        let totalRelatedListingsVisible = relatedListingElements.length

        if (totalRelatedListingsVisible - totalRelatedListingsToHide < 3) {
          if (totalRelatedListingsVisible < 3) {
            return false
          } else {
            totalRelatedListingsToHide = totalRelatedListingsToHide - 3
          }
        }

        if (totalRelatedListingsToHide === 0) {
          return false
        }

        let elementsToRemove = relatedListingElements.slice(-totalRelatedListingsToHide)

        elementsToRemove.forEach(element => {
          element.remove()
        })
      } else if (difference >= 0 && difference < 100) {
        this.isSidebarTooLong = true
      } else {
        this.isSidebarTooLong = false
      }
    },

    reportListing() {
      this.$analytics.addEvent({
        category: 'listing',
        action: 'Clicked report listing button'
      })

      this.$store.dispatch('modal/showModal', {
        modalName: 'ModalReport',
        modalData: {
          entity: {
            ...this.listing,
            featureImageRef: this.listing.images.length && this.listing.images[0].fileRef
          },
          entityType: 'listing',
          title: this.compTitle
        }
      })
    },

    createSidebarScrollObserver() {
      if (!('IntersectionObserver' in window) || !this.$refs.listingOverview || !this.$refs.listingOverview.$el) {
        return
      }

      // Watch the sidebar and if we scroll past the related listings section, sticky the overview section
      this.sidebarObserver = new IntersectionObserver(
        entries => {
          const relatedDiv = entries[0]

          if (relatedDiv.isIntersecting) {
            this.showSidebarStickyOverview = false
          } else {
            this.showSidebarStickyOverview = true
          }
        },
        {
          threshold: 0.1
        }
      )

      this.sidebarObserver.observe(this.$refs.listingOverview.$el)
    },

    destroySidebarScrollObserver() {
      if (!('IntersectionObserver' in window) || !this.sidebarObserver) {
        return
      }

      this.sidebarObserver.disconnect()
    },

    async onAuctionAskQuestionButtonClick() {
      this.$modal.open('ModalContactSupport', {
        noRedirect: true,
        label: 'Have a question about the livestock listed for this auction. Ask away.',
        hint:
          'We look to respond to you with an answer within 2 business hours of sending. The response will be sent to the email used for your account.'
      })
    }
  },
  head: {
    title() {
      return {
        inner: this.compTitle || 'Listing'
      }
    },
    meta() {
      return generateMetaTags({
        title: this.compTitle,
        description: this.compDescription,
        image: this.compOgImage
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@media (min-width: 1024px) {
  .listing-right-column-container {
    max-width: 430px;
    min-width: 370px;
  }
}

@media (min-width: 1280px) {
  .listing-right-column-container {
    max-width: 520px;
    min-width: 430px;
  }
}
</style>
